import { api } from "./api"
import type { BaseQueryType } from "./api"
import _ from "lodash"
import { useLangRefetch } from "./utils"
import type { components, WithId } from "./generated"
import type { TypedUseQueryHookResult } from "@reduxjs/toolkit/query/react"

export type TemplateRole = WithId<components["schemas"]["TemplateRole"]>

type TemplateRoleBody = Partial<components["schemas"]["TemplateRole"]>

const templateRoleApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getTemplateRoles: builder.query<TemplateRole[], { noLoc?: boolean } | void>({
			query: (params) => ({
				url: "/templateRole",
				params: params ?? undefined,
			}),
			providesTags: (result, error) => {
				if (error || !result) return []
				return [
					"Loc",
					{ type: "TemplateRole", id: "LIST" },
					...result.map((doc) => ({ type: "TemplateRole" as const, id: doc._id })),
				]
			},
		}),
		getTemplateRole: builder.query<TemplateRole, { id: string; noLoc?: boolean }>({
			query: ({ id, noLoc }) => ({
				url: `/templateRole/${id}`,
				params: {
					noLoc,
				},
			}),
			providesTags: (_result, _error, arg) => {
				return [{ type: "TemplateRole", id: arg.id }]
			},
		}),
		createTemplateRole: builder.mutation<TemplateRole, Omit<TemplateRoleBody, "kind"> & { kind?: string | null }>({
			query: (body) => ({
				url: "/templateRole",
				method: "POST",
				body,
			}),
			invalidatesTags: () => {
				return [{ type: "TemplateRole", id: "LIST" }]
			},
		}),
		updateTemplateRole: builder.mutation<
			TemplateRole,
			{ id: string; body: TemplateRoleBody & { createRootTemplates?: boolean; updateRootTemplates?: boolean } }
		>({
			query: ({ id, body }) => ({
				url: `/templateRole/${id}`,
				method: "PUT",
				body,
			}),
			invalidatesTags: (_result, _error, args) => {
				return _.compact([
					{ type: "TemplateRole", id: args.id },
					(args?.body?.createRootTemplates || args?.body?.updateRootTemplates) && { type: "RootGroup" },
				])
			},
		}),
		removeTemplateRole: builder.mutation<void, { id: string; body?: { removeAll?: boolean } }>({
			query: ({ id, body }) => ({
				url: `/templateRole/${id}`,
				method: "DELETE",
				body,
			}),
			invalidatesTags: (_result, _error, args) => {
				return _.compact([{ type: "TemplateRole", id: args.id }, args?.body?.removeAll && { type: "RootGroup" }])
			},
		}),
		rootGroupAddTemplateRole: builder.mutation<void, { id: string; templateId: string }>({
			query: ({ id, templateId }) => ({
				url: `/root_group/${id}/templateRole/${templateId}`,
				method: "POST",
			}),
			invalidatesTags: (_result, _error, args) => {
				return [{ type: "RootGroup", id: args.id }]
			},
		}),
		rootGroupUpdateTemplateRole: builder.mutation<void, { id: string; templateId: string; body: TemplateRoleBody }>({
			query: ({ id, templateId, body }) => ({
				url: `/root_group/${id}/templateRole/${templateId}`,
				method: "PUT",
				body,
			}),
			invalidatesTags: (_result, _error, args) => {
				return [{ type: "RootGroup", id: args.id }]
			},
		}),
		rootGroupRemoveTemplateRole: builder.mutation<void, { id: string; templateId: string }>({
			query: ({ id, templateId }) => ({
				url: `/root_group/${id}/templateRole/${templateId}`,
				method: "DELETE",
			}),
			invalidatesTags: (_result, _error, args) => {
				return [{ type: "RootGroup", id: args.id }]
			},
		}),
		rgcAddTemplateRole: builder.mutation<void, { id: string; templateId: string }>({
			query: ({ id, templateId }) => ({
				url: `/rootGroupClass/${id}/templateRole/${templateId}`,
				method: "POST",
			}),
			invalidatesTags: (_result, _error, args) => {
				return [{ type: "RootGroupClass", id: args.id }]
			},
		}),
		rgcUpdateTemplateRole: builder.mutation<void, { id: string; templateId: string; body: TemplateRoleBody }>({
			query: ({ id, templateId, body }) => ({
				url: `/rootGroupClass/${id}/templateRole/${templateId}`,
				method: "PUT",
				body,
			}),
			invalidatesTags: (_result, _error, args) => {
				return [{ type: "RootGroupClass", id: args.id }]
			},
		}),
		rgcRemoveTemplateRole: builder.mutation<void, { id: string; templateId: string }>({
			query: ({ id, templateId }) => ({
				url: `/rootGroupClass/${id}/templateRole/${templateId}`,
				method: "DELETE",
			}),
			invalidatesTags: (_result, _error, args) => {
				return [{ type: "RootGroupClass", id: args.id }]
			},
		}),
	}),
})

export const {
	useGetTemplateRolesQuery,
	useGetTemplateRoleQuery,
	useCreateTemplateRoleMutation,
	useUpdateTemplateRoleMutation,
	useRemoveTemplateRoleMutation,
	useRootGroupAddTemplateRoleMutation,
	useRootGroupUpdateTemplateRoleMutation,
	useRootGroupRemoveTemplateRoleMutation,
	useRgcAddTemplateRoleMutation,
	useRgcUpdateTemplateRoleMutation,
	useRgcRemoveTemplateRoleMutation,
} = templateRoleApi

export const useGetTemplateRolesQueryL = (
	arg?: null | object,
	options?: Record<string, any>
): TypedUseQueryHookResult<TemplateRole[], { noLoc?: boolean } | void, BaseQueryType> =>
	useLangRefetch(useGetTemplateRolesQuery, arg, options)
